import React, { useState, useEffect } from 'react';
import { fileUrl } from '../utils/fileUrl';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Calendar, Clock, Eye, ArrowLeft, Tag, Share2, Facebook, Twitter, Linkedin, Instagram, Link2, Check } from 'lucide-react';
import { Helmet } from 'react-helmet';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    try {
      const response = await axios.get(`${API}/api/blog/posts/${slug}`);
      setPost(response.data);
      
      // Fetch related posts from the same category
      if (response.data.category) {
        const relatedResponse = await axios.get(
          `${API}/api/blog/posts?category=${encodeURIComponent(response.data.category)}&limit=3`
        );
        setRelatedPosts(relatedResponse.data.filter(p => p.slug !== slug).slice(0, 2));
      }
    } catch (error) {
      console.error('Failed to load blog post:', error);
      navigate('/blog');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const estimateReadTime = (content) => {
    if (!content) return '1 min';
    const wordCount = content.replace(/<[^>]*>/g, '').split(/\s+/).length;
    const minutes = Math.ceil(wordCount / 200);
    return `${minutes} min read`;
  };

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <header className="bg-black text-white py-6">
          <div className="max-w-4xl mx-auto px-4">
            <Link to="/" className="inline-block">
              <img 
                src="/onyx-reserve-logo.png" 
                alt="Onyx Reserve" 
                className="h-12"
              />
            </Link>
          </div>
        </header>
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="text-center py-12">Loading...</div>
        </div>
      </div>
    );
  }

  if (!post) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Dynamic SEO Meta Tags */}
      <Helmet>
        <title>{post.title} | Onyx Reserve Blog</title>
        <meta name="description" content={post.meta_description || post.excerpt} />
        <meta name="keywords" content={post.keywords?.join(', ')} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.meta_description || post.excerpt} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={shareUrl} />
        {post.featured_image && <meta property="og:image" content={post.featured_image} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.meta_description || post.excerpt} />
        <link rel="canonical" href={shareUrl} />
        
        {/* Article structured data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": post.title,
            "description": post.meta_description || post.excerpt,
            "image": post.featured_image,
            "author": {
              "@type": "Person",
              "name": post.author_name || "Onyx Reserve"
            },
            "publisher": {
              "@type": "Organization",
              "name": "Onyx Reserve",
              "logo": {
                "@type": "ImageObject",
                "url": "/onyx-reserve-logo.png"
              }
            },
            "datePublished": post.published_at,
            "dateModified": post.updated_at,
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": shareUrl
            }
          })}
        </script>
      </Helmet>

      {/* Header */}
      <header className="bg-black text-white py-6">
        <div className="max-w-4xl mx-auto px-4 flex items-center justify-between">
          <Link to="/">
            <img 
              src="/onyx-reserve-logo.png" 
              alt="Onyx Reserve" 
              className="h-12"
            />
          </Link>
          <Link to="/blog" className="text-gray-300 hover:text-white flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to Blog
          </Link>
        </div>
      </header>

      {/* Featured Image */}
      {post.featured_image && (
        <div className="w-full h-64 md:h-96 bg-gray-200">
          <img 
            src={fileUrl(post.featured_image)} 
            alt={post.title}
            className="w-full h-full object-cover"
          />
        </div>
      )}

      <article className="max-w-4xl mx-auto px-4 py-8">
        {/* Article Header */}
        <header className="mb-8">
          <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500 mb-4">
            {post.category && (
              <Link to={`/blog?category=${encodeURIComponent(post.category)}`}>
                <Badge variant="outline">{post.category}</Badge>
              </Link>
            )}
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {formatDate(post.published_at)}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {estimateReadTime(post.content)}
            </span>
            <span className="flex items-center gap-1">
              <Eye className="w-4 h-4" />
              {post.views || 0} views
            </span>
          </div>

          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {post.title}
          </h1>

          {post.excerpt && (
            <p className="text-xl text-gray-600 leading-relaxed">
              {post.excerpt}
            </p>
          )}

          <div className="mt-4 text-sm text-gray-500">
            By <span className="font-medium">{post.author_name || 'Onyx Reserve'}</span>
          </div>
        </header>

        {/* Article Content */}
        <div 
          className="prose prose-lg max-w-none mb-8"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />

        {/* Tags */}
        {post.tags && post.tags.length > 0 && (
          <div className="border-t border-b py-4 my-8">
            <div className="flex flex-wrap items-center gap-2">
              <Tag className="w-4 h-4 text-gray-500" />
              {post.tags.map((tag, idx) => (
                <Badge key={idx} variant="secondary" className="text-sm">
                  {tag}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Share Buttons */}
        <div className="my-8 p-6 bg-gray-50 rounded-xl">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Share2 className="w-5 h-5" />
            Share this article
          </h3>
          <div className="flex flex-wrap gap-3">
            {/* Facebook */}
            <a 
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-[#1877F2] text-white rounded-lg hover:bg-[#166FE5] transition-colors"
            >
              <Facebook className="w-5 h-5" />
              <span className="hidden sm:inline">Facebook</span>
            </a>
            
            {/* LinkedIn */}
            <a 
              href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(post.title)}&summary=${encodeURIComponent(post.excerpt || post.meta_description || '')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-[#0A66C2] text-white rounded-lg hover:bg-[#004182] transition-colors"
            >
              <Linkedin className="w-5 h-5" />
              <span className="hidden sm:inline">LinkedIn</span>
            </a>
            
            {/* Instagram - Copy link for sharing */}
            <button 
              onClick={() => {
                navigator.clipboard.writeText(shareUrl);
                setCopied(true);
                toast.success('Link copied! Paste it in your Instagram bio or story.');
                setTimeout(() => setCopied(false), 3000);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#F77737] text-white rounded-lg hover:opacity-90 transition-opacity"
            >
              <Instagram className="w-5 h-5" />
              <span className="hidden sm:inline">{copied ? 'Copied!' : 'Instagram'}</span>
            </button>
            
            {/* Twitter/X */}
            <a 
              href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(post.title)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
            >
              <Twitter className="w-5 h-5" />
              <span className="hidden sm:inline">X (Twitter)</span>
            </a>
            
            {/* Copy Link */}
            <button 
              onClick={() => {
                navigator.clipboard.writeText(shareUrl);
                setCopied(true);
                toast.success('Link copied to clipboard!');
                setTimeout(() => setCopied(false), 3000);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              {copied ? <Check className="w-5 h-5" /> : <Link2 className="w-5 h-5" />}
              <span className="hidden sm:inline">{copied ? 'Copied!' : 'Copy Link'}</span>
            </button>
          </div>
        </div>

        {/* CTA */}
        <Card className="bg-black text-white my-8">
          <CardContent className="p-8 text-center">
            <h3 className="text-2xl font-bold mb-4">Ready for Your Luxury Ride?</h3>
            <p className="text-gray-300 mb-6">
              Experience Miami in style with Onyx Reserve's premium black car service.
            </p>
            <Link to="/register?role=rider">
              <Button className="bg-gold-500 text-black hover:bg-gold-400 text-lg px-8 py-6">
                Book Your Ride Now
              </Button>
            </Link>
          </CardContent>
        </Card>

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <div className="mt-12">
            <h3 className="text-2xl font-bold mb-6">Related Articles</h3>
            <div className="grid md:grid-cols-2 gap-6">
              {relatedPosts.map((relatedPost) => (
                <Card key={relatedPost.id} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-4">
                    <Badge variant="outline" className="text-xs mb-2">
                      {relatedPost.category}
                    </Badge>
                    <h4 className="font-bold mb-2 hover:text-blue-600">
                      <Link to={`/blog/${relatedPost.slug}`}>
                        {relatedPost.title}
                      </Link>
                    </h4>
                    <p className="text-sm text-gray-600 line-clamp-2">
                      {relatedPost.excerpt}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </article>

      {/* Footer */}
      <footer className="bg-black text-white py-8 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <img 
            src="/onyx-reserve-logo.png" 
            alt="Onyx Reserve" 
            className="h-12 mx-auto mb-4"
          />
          <p className="text-gray-400">
            © {new Date().getFullYear()} Onyx Reserve. Premium Black Car Service in Miami.
          </p>
          <div className="mt-4 flex justify-center gap-6">
            <Link to="/" className="text-gray-400 hover:text-white">Home</Link>
            <Link to="/blog" className="text-gray-400 hover:text-white">Blog</Link>
            <Link to="/login" className="text-gray-400 hover:text-white">Login</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BlogPost;
